import { useMemo } from "react";

import { BTCheckbox } from "@btnet/core-components";

import { CheckAllState } from "types/enum";

import { getCheckAllState } from "utilities/array/array";

import { type IGridRowData } from "commonComponents/utilities/Grid/GridContainer.types";

interface IGridCardViewActionBarProps {
    rowData: IGridRowData[];
    onSelectAllToggle: (checked: boolean) => void;
}

/**
 * Sticky-ish action bar above the card list with the select-all checkbox and
 * the selected-count text. Bulk action buttons themselves render via the
 * existing `GridContainer` toolbar (CheckedActions) — this bar is just the
 * card-view counterpart of the column-header select cell.
 */
export const GridCardViewActionBar: React.FC<IGridCardViewActionBarProps> = ({
    rowData,
    onSelectAllToggle,
}) => {
    const checkAllState = useMemo(
        () => getCheckAllState(rowData, (row) => !!row.isSelected),
        [rowData],
    );
    const selectedCount = useMemo(() => rowData.filter((row) => row.isSelected).length, [rowData]);

    const isAllChecked = checkAllState === CheckAllState.AllChecked;
    const isIndeterminate = checkAllState === CheckAllState.Indeterminate;

    return (
        <div className="GridCardView-actionBar">
            <BTCheckbox
                id="GridCardView-selectAll"
                data-testid="GridCardView-selectAll"
                checked={isAllChecked}
                indeterminate={isIndeterminate}
                disabled={rowData.length === 0}
                onChange={(e) => onSelectAllToggle(e.target.checked)}
            >
                <span className="GridCardView-actionBarLabel">
                    {selectedCount > 0 ? `${selectedCount} selected` : "Select all"}
                </span>
            </BTCheckbox>
        </div>
    );
};
